import { Button, Modal } from "@xola/ui-kit";
import React from "react";
import { BaseSchedule, Event, Experience, Guide, Permission } from "types";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useResource } from "../../../hooks/useResource";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { EventSummaryTile } from "../../Event/EventSummaryTile";
import { Loader } from "../../Loader";

interface ConfirmDeleteGuideScheduleModalProps {
    events?: ID[];
    onClose: () => void;
    guide: Guide;
    schedule?: BaseSchedule;
    onConfirm: (schedule: BaseSchedule) => void;
}

export const ConfirmDeleteGuideScheduleModal = ({
    events = [],
    guide,
    onClose,
    schedule,
    onConfirm,
}: ConfirmDeleteGuideScheduleModalProps) => {
    const { isGranted } = useAuthorization();
    const hasRequiredPermission = isGranted(Permission.RESOURCES_GUIDES_ALL_VIEW);
    const handleDelete = () => {
        if (!hasRequiredPermission) {
            onClose();
        }

        if (schedule && hasRequiredPermission) {
            onConfirm(schedule);
        }
    };

    return (
        <Modal shouldCloseOnOutsideClick size="large" isOpen={events.length > 0} onClose={onClose}>
            <Modal.Header>Cannot delete schedule</Modal.Header>

            <Modal.Body className="space-y-3 text-gray-darker">
                {hasRequiredPermission ? (
                    <p>
                        Sorry, this schedule could not be deleted because <var data-var="guideName">{guide.name}</var>{" "}
                        is assigned to:
                    </p>
                ) : (
                    <p>Sorry, this schedule could not be deleted because you are assigned to:</p>
                )}

                {events.map((eventId) => (
                    <ConflictingEvent key={eventId} eventId={eventId} />
                ))}

                {hasRequiredPermission ? (
                    <p className="mt-2">
                        In order to delete the schedule, the assigned guide must be removed from conflicting events.
                    </p>
                ) : (
                    <p className="mt-2">Please contact your manager to help make changes to your schedule.</p>
                )}
            </Modal.Body>

            <Modal.Footer className="space-x-4">
                {hasRequiredPermission && <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>}

                <Button color="danger" onClick={handleDelete}>
                    {hasRequiredPermission ? "Unassign guide and delete schedule" : "OK"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

interface ConflictingEventProps {
    eventId: ID;
}

const ConflictingEvent = ({ eventId }: ConflictingEventProps) => {
    const { data: event } = useResource<Event>(["events", eventId]);
    const { data: experience } = useResource<Experience>(["products", event?.experience?.id]);

    if (!event || !experience) {
        return <Loader />;
    }

    return <EventSummaryTile event={event} />;
};
